import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import { defineComponent } from "vue";
import { p } from "@/utils/props-builder";
import Skeleton from "./Skeleton";

const props = {
  /**
   * Show info panel on the right
   * @default true
   */
  showInfoPanel: p.boolean(true),

  /**
   * Number of thread items in list
   * @default 10
   */
  threadCount: p.number(10),

  /**
   * Number of messages in conversation
   * @default 6
   */
  messageCount: p.number(6),
} satisfies ComponentPropsOptions;

export type AgentThreadsSkeletonProps = ExtractPublicPropTypes<typeof props>;

/**
 * AgentThreadsSkeleton - Loading placeholder for agent threads/chat page
 *
 * Three-column layout specialized for the agent chat playground:
 * - Left: Threads list with search and filters
 * - Center: Conversation area with messages
 * - Right: Thread info panel (optional)
 *
 * @example
 * ```tsx
 * // Basic usage
 * <AgentThreadsSkeleton />
 *
 * // Without info panel
 * <AgentThreadsSkeleton showInfoPanel={false} />
 *
 * // Custom counts
 * <AgentThreadsSkeleton threadCount={8} messageCount={6} />
 *
 * // With QueryLoader
 * <QueryLoader queries={[agentQuery, threadsQuery]}>
 *   <QueryLoader.Loading>
 *     <AgentThreadsSkeleton />
 *   </QueryLoader.Loading>
 *   <div class="flex h-full">
 *     ...
 *   </div>
 * </QueryLoader>
 * ```
 */
const AgentThreadsSkeleton = defineComponent({
  name: "AgentThreadsSkeleton",
  props,
  setup(props) {
    return () => (
      <div class="flex h-[calc(100vh-5.25rem)] w-full overflow-hidden rounded-lg bg-card">
        {/* Left: Threads list */}
        <div class="flex w-72 flex-none flex-col border-r">
          {/* Toolbar */}
          <div class="h-12 space-y-2 border-b p-2">
            <Skeleton class="h-full w-full rounded-md" />
          </div>

          {/* Thread list items */}
          <div class="flex-1 space-y-1 overflow-auto p-2">
            {Array.from({ length: props.threadCount }).map((_, i) => (
              <div
                key={`thread-${i}`}
                class="hover:bg-accent space-y-2 rounded-md border bg-card p-3"
              >
                <div class="flex items-start justify-between gap-2">
                  <Skeleton class="h-8 flex-1 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Center: Conversation */}
        <div class="flex grow flex-col">
          {/* Conversation header */}
          <div class="flex h-12 items-center justify-between border-b px-4">
            <div class="flex items-center gap-2">
              <Skeleton class="h-8 w-8 rounded-full" />
              <Skeleton class="h-5 w-32 rounded" />
            </div>
            <div class="flex gap-2">
              <Skeleton class="h-8 w-8 rounded-md" />
              <Skeleton class="h-8 w-8 rounded-md" />
            </div>
          </div>

          {/* Messages */}
          <div class="flex-1 space-y-4 overflow-auto p-6">
            {Array.from({ length: props.messageCount }).map((_, i) => {
              const isUser = i % 2 === 0;
              return (
                <div
                  key={`message-${i}`}
                  class={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}
                >
                  {!isUser && <Skeleton class="h-32 w-72 shrink-0" />}
                  {isUser && <Skeleton class="h-12 w-56 shrink-0" />}
                  {/* <div class={`space-y-2 ${isUser ? "max-w-[70%]" : "max-w-[80%]"}`}></div> */}
                  {/* {isUser && <Skeleton class="h-12 w-8 shrink-0 rounded-full" />} */}
                </div>
              );
            })}
          </div>

          {/* Input area */}
          <div class="border-t p-4">
            <Skeleton class="h-20 w-full rounded-md" />
          </div>
        </div>

        {/* Right: Info panel (optional) */}
        {props.showInfoPanel && (
          <div class="flex w-72 shrink-0 flex-col border-l">
            {/* Panel header */}
            <div class="flex h-12 items-center justify-between border-b px-4">
              <Skeleton class="h-4 w-24 rounded" />
              <Skeleton class="h-6 w-6 rounded" />
            </div>

            {/* Panel content */}
            <div class="flex-1 space-y-6 overflow-auto p-4">
              {/* Section 1 */}
              <div class="space-y-3">
                <Skeleton class="h-4 w-20 rounded" />
                <div class="space-y-2">
                  <Skeleton class="h-3 w-full rounded" />
                  <Skeleton class="h-3 w-4/5 rounded" />
                </div>
              </div>

              {/* Section 2 */}
              <div class="space-y-3">
                <Skeleton class="h-4 w-24 rounded" />
                <div class="space-y-2">
                  <Skeleton class="h-8 w-full rounded-md" />
                  <Skeleton class="h-8 w-full rounded-md" />
                </div>
              </div>

              {/* Section 3 */}
              <div class="space-y-3">
                <Skeleton class="h-4 w-28 rounded" />
                <Skeleton class="h-20 w-full rounded-md" />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
});

export default AgentThreadsSkeleton;
